import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import TextField from '@mui/material/TextField';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useApi } from '../../services/api';
import CompanyLogo from '../CompanyLogo';

export interface CreatedCompanyData {
  companyName: string;
  id: number;
  url?: string;
}

interface CreateCompanyModalProps {
  onClose: () => void;
  onCompanyCreated: (company: CreatedCompanyData) => void;
  open: boolean;
}

interface CompanyMetadata {
  companyName: string;
  cikSuggestion?: { cik: string | null; companyName: string };
  descriptionSuggestion?: string;
  domainSuggestion?: string;
  fiscalYearEndMonthSuggestion?: number;
  formattedNameSuggestion?: string;
  gicsCodeSuggestion?: { code: string; name: string };
  headcountSuggestion?: number;
  headquartersSuggestion?: { city: string; country: string; state?: string };
  revenueSuggestion?: number;
}

/**
 * Strips a raw URL/domain input down to just the top-level domain.
 * e.g. "https://www.salesforce.com/products" → "salesforce.com"
 */
function extractDomain(input: string): string {
  let cleaned = input.trim().toLowerCase();

  // Strip protocol
  cleaned = cleaned.replace(/^https?:\/\//, '');

  // Strip path, query, fragment
  cleaned = cleaned.split('/')[0];
  cleaned = cleaned.split('?')[0];
  cleaned = cleaned.split('#')[0];

  // Strip port
  cleaned = cleaned.split(':')[0];

  // Strip subdomains (www., mail., app., etc.): keep last two parts
  // Handle known multi-part TLDs like .co.uk, .com.au
  const parts = cleaned.split('.');
  const multiPartTlds = ['co.uk', 'co.jp', 'com.au', 'com.br', 'co.in', 'co.nz', 'co.za', 'org.uk', 'net.au'];
  const lastTwo = parts.slice(-2).join('.');

  if (parts.length > 2 && multiPartTlds.includes(lastTwo)) {
    return parts.slice(-3).join('.');
  }
  if (parts.length > 2) {
    return parts.slice(-2).join('.');
  }

  return cleaned;
}

function formatRevenue(revenue: number): string {
  if (revenue >= 1_000_000_000) {
    return `$${(revenue / 1_000_000_000).toFixed(1)}B`;
  }
  if (revenue >= 1_000_000) {
    return `$${(revenue / 1_000_000).toFixed(0)}M`;
  }
  if (revenue >= 1_000) {
    return `$${(revenue / 1_000).toFixed(0)}K`;
  }
  return `$${revenue}`;
}

function formatHeadcount(headcount: number): string {
  if (headcount >= 1_000) {
    return `${(headcount / 1_000).toFixed(1).replace(/\.0$/, '')}K`;
  }
  return headcount.toLocaleString();
}

function formatHeadquarters(hq: { city: string; country: string; state?: string }): string {
  const parts = [hq.city, hq.state, hq.country].filter(Boolean);
  return parts.join(', ');
}

function MetadataRow({ label, value }: { label: string; value: string }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, flex: 1, minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
        {label}
      </Typography>
      <Typography variant="body2">
        {value}
      </Typography>
    </Box>
  );
}

export function CreateCompanyModal({
  open,
  onClose,
  onCompanyCreated,
}: CreateCompanyModalProps) {
  const api = useApi();
  const [domainInput, setDomainInput] = useState('');
  const [debouncedDomain, setDebouncedDomain] = useState('');
  const [isLooking, setIsLooking] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [metadata, setMetadata] = useState<CompanyMetadata | null>(null);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Debounce domain input for logo with 3 second delay
  useEffect(() => {
    const extracted = domainInput.trim() ? extractDomain(domainInput) : '';
    if (!extracted || !extracted.includes('.')) {
      setDebouncedDomain('');
      return;
    }
    const timer = setTimeout(() => {
      setDebouncedDomain(extracted);
    }, 2000);
    return () => clearTimeout(timer);
  }, [domainInput]);

  // Derive the logo domain: use the looked-up domain if available, otherwise the debounced input
  const logoDomain = useMemo(() => {
    if (metadata?.domainSuggestion) return metadata.domainSuggestion;
    return debouncedDomain;
  }, [metadata, debouncedDomain]);

  const resetForm = useCallback(() => {
    setDomainInput('');
    setDebouncedDomain('');
    setMetadata(null);
    setLookupError(null);
    setSubmitError(null);
    setIsLooking(false);
    setIsSubmitting(false);
  }, []);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [onClose, resetForm]);

  const handleLookup = useCallback(async () => {
    const domain = extractDomain(domainInput);
    if (!domain || !domain.includes('.')) {
      setLookupError('Please enter a valid domain (e.g. salesforce.com)');
      return;
    }

    // Immediately update the logo domain (skip the debounce)
    setDebouncedDomain(domain);
    setIsLooking(true);
    setLookupError(null);
    setMetadata(null);
    setSubmitError(null);

    try {
      const result = await api.post('ai/get-company-metadata-suggestions', {
        companyDomain: domain,
      });

      if (result.error) {
        setLookupError(result.message || 'Failed to look up company information');
        return;
      }

      // Check for a valid company - the formattedNameSuggestion is the key indicator
      if (!result.formattedNameSuggestion) {
        setLookupError(`We couldn't find a valid company at "${domain}". Please check the domain and try again.`);
        return;
      }

      setMetadata(result as CompanyMetadata);
    }
    catch (err: any) {
      console.error('Error looking up company:', err);
      setLookupError(err?.message || 'Failed to look up company information. Please try again.');
    }
    finally {
      setIsLooking(false);
    }
  }, [api, domainInput]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isLooking && !metadata) {
      e.preventDefault();
      handleLookup();
    }
  }, [handleLookup, isLooking, metadata]);

  const handleSave = useCallback(async () => {
    if (!metadata?.formattedNameSuggestion) return;

    setIsSubmitting(true);
    setSubmitError(null);

    const domain = extractDomain(domainInput);

    try {
      const response = await api.post('/company', {
        companyName: metadata.formattedNameSuggestion,
        url: metadata.domainSuggestion || domain,
        profile: metadata.descriptionSuggestion || undefined,
        stockTicker: undefined,
        city: metadata.headquartersSuggestion?.city || undefined,
        state: metadata.headquartersSuggestion?.state || undefined,
        country: metadata.headquartersSuggestion?.country || undefined,
        revenue: metadata.revenueSuggestion || undefined,
        headcount: metadata.headcountSuggestion || undefined,
        gicsCode: metadata.gicsCodeSuggestion?.code || undefined,
        cik: metadata.cikSuggestion?.cik || undefined,
        fiscalYearEndMonth: metadata.fiscalYearEndMonthSuggestion || undefined,
      });

      if (response.error) {
        setSubmitError(response.message || 'Failed to create company');
        return;
      }

      onCompanyCreated({
        id: response.id,
        companyName: response.companyName,
        url: response.url,
      });
      handleClose();
    }
    catch (err: any) {
      console.error('Error creating company:', err);
      setSubmitError(err?.message || 'Failed to create company. Please try again.');
    }
    finally {
      setIsSubmitting(false);
    }
  }, [api, domainInput, metadata, onCompanyCreated, handleClose]);

  const handleStartOver = useCallback(() => {
    setMetadata(null);
    setLookupError(null);
    setSubmitError(null);
    setDomainInput('');
    setDebouncedDomain('');
  }, []);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle
        sx={{
          fontSize: '1.25rem',
          fontWeight: 700,
          position: 'relative',
          pb: 1,
        }}
      >
        Create Company
        <IconButton
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {/* Domain input with logo */}
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
            <Box sx={{ pt: 1, flexShrink: 0 }}>
              {logoDomain ? (
                <CompanyLogo
                  companyName={logoDomain}
                  companyUrl={logoDomain}
                  sx={{ width: 40, height: 40, borderRadius: 1 }}
                />
              ) : (
                <Avatar
                  variant="rounded"
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 1,
                    bgcolor: 'grey.200',
                    color: 'grey.400',
                    fontSize: '1rem',
                  }}
                >
                  ?
                </Avatar>
              )}
            </Box>
            <TextField
              autoFocus
              label="Company Domain"
              value={domainInput}
              onChange={(e) => {
                setDomainInput(e.target.value);
                if (lookupError) setLookupError(null);
              }}
              onKeyDown={handleKeyDown}
              fullWidth
              placeholder="e.g. salesforce.com"
              disabled={isLooking || !!metadata}
              helperText={
                metadata
                  ? `Domain: ${extractDomain(domainInput)}`
                  : 'Enter a company domain to look up its details'
              }
              error={!!lookupError}
              data-testid="target-company-domain-input"
            />
            {!metadata && (
              <Button
                variant="contained"
                onClick={handleLookup}
                disabled={isLooking || !domainInput.trim()}
                sx={{ mt: 1, height: 40, minWidth: 40, px: 1 }}
                data-testid="target-company-lookup-button"
              >
                {isLooking ? <CircularProgress size={16} /> : <SearchIcon />}
              </Button>
            )}
          </Box>

          {/* Lookup error */}
          {lookupError && (
            <Alert severity="warning">
              {lookupError}
            </Alert>
          )}

          {/* Loading indicator */}
          {isLooking && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 2 }}>
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">
                Looking up company information...
              </Typography>
            </Box>
          )}

          {/* Metadata review (read-only, non-editable display) */}
          {metadata && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                bgcolor: 'action.hover',
                borderRadius: 1.5,
                p: 2,
              }}
            >
              <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
                Company Details
              </Typography>

              {metadata.formattedNameSuggestion && (
                <MetadataRow label="Company Name" value={metadata.formattedNameSuggestion} />
              )}

              {metadata.descriptionSuggestion && (
                <MetadataRow label="Description" value={metadata.descriptionSuggestion} />
              )}

              <Box sx={{ display: 'flex', gap: 3 }}>
                {metadata.headquartersSuggestion && (
                  <MetadataRow label="Headquarters" value={formatHeadquarters(metadata.headquartersSuggestion)} />
                )}
                {metadata.gicsCodeSuggestion?.name && (
                  <MetadataRow label="Industry" value={metadata.gicsCodeSuggestion.name} />
                )}
              </Box>

              <Box sx={{ display: 'flex', gap: 3 }}>
                {metadata.revenueSuggestion != null && (
                  <MetadataRow label="Annual Revenue" value={formatRevenue(metadata.revenueSuggestion)} />
                )}
                {metadata.headcountSuggestion != null && (
                  <MetadataRow label="Employees" value={formatHeadcount(metadata.headcountSuggestion)} />
                )}
              </Box>

              {submitError && (
                <Alert severity="error">
                  {submitError}
                </Alert>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        {metadata
          ? (
              <>
                <Button onClick={handleStartOver} disabled={isSubmitting} color="inherit">
                  Start Over
                </Button>
                <Box sx={{ flex: 1 }} />
                <Button onClick={handleClose} disabled={isSubmitting}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSave}
                  disabled={isSubmitting}
                  startIcon={isSubmitting ? <CircularProgress size={16} /> : undefined}
                >
                  {isSubmitting ? 'Adding...' : 'Looks Good, Add Company'}
                </Button>
              </>
            )
          : (
              <Button onClick={handleClose}>
                Cancel
              </Button>
            )}
      </DialogActions>
    </Dialog>
  );
}

export default CreateCompanyModal;
